import { AppSidebar } from '@components/app-sidebar';
import { useAuth } from '@components/auth/AuthContext';
import { cn } from '@lib/utils';
import { SidebarProvider, SidebarTrigger } from '@ui/sidebar';
import { Menu } from 'lucide-react';
import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

export const AppLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Define public pages that don't require authentication
  // Remove trailing slashes for comparison to handle both /path and /path/
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isPublicPage =
    [
      '/login',
      '/signup',
      '/email-verification',
      '/reset-password',
      '/accounts/signup',
      '/reset-password/confirm',
      '/email-verification-status',
      '/no-active-company',
      '/complete-signup',
      '/demo',
    ].includes(normalizedPathname) ||
    // Public shared generated-offer links (/o/<token>, JOB-184).
    normalizedPathname.startsWith('/o/');

  // Special case: root path (landing page) is always public
  const isLandingPage = location.pathname === '/';

  // Shared generated-offer letters render their own complete letter footer
  // (company mark + Powered by ClearOffer); stacking the generic copyright
  // footer under it gave candidates two footers.
  const isGeneratedOfferPage = normalizedPathname.startsWith('/o/');

  // If it's the landing page or a public page, no auth required
  const shouldRenderUnauthenticated = isLandingPage || isPublicPage;

  // Authenticated but chrome-free. The merge-field resolver is a two-pane
  // workspace with its own masthead: a document beside the decisions needs the
  // full width, and a second navigation rail beside a first one is just noise.
  // Still gated on auth — this suppresses the sidebar, never the login check.
  const isFullBleedWorkspace =
    /^\/offers\/[^/]+\/documents\/resolve$/.test(normalizedPathname) ||
    /^\/company\/document-templates\/[^/]+$/.test(normalizedPathname);

  // Redirect authenticated users from login page to dashboard
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/login') {
      navigate('/dashboard');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  return (
    <SidebarProvider>
      <div
        className={cn('flex flex-col bg-background min-h-screen w-full overflow-hidden')}
        data-testid="app-layout-container"
      >
        <div className="flex flex-grow w-full relative z-10">
          {isAuthenticated && !shouldRenderUnauthenticated && isFullBleedWorkspace ? (
            <main className="flex-grow w-full" data-testid="full-bleed-workspace">
              <Outlet />
            </main>
          ) : isAuthenticated && !shouldRenderUnauthenticated ? (
            <div className="flex w-full" data-testid="authenticated-layout">
              <AppSidebar />
              <div className="flex-1 flex flex-col min-w-0 w-full">
                {/* Mobile header with sidebar trigger - only visible on mobile */}
                <header
                  className="md:hidden flex items-center h-14 px-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40"
                  data-testid="mobile-header"
                >
                  <SidebarTrigger className="h-9 w-9" data-testid="mobile-sidebar-trigger">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle navigation menu</span>
                  </SidebarTrigger>
                  <span className="ml-3 font-semibold text-lg">ClearOffer</span>
                </header>
                <main className="flex-1 min-w-0 w-full" data-testid="main-content">
                  <Outlet />
                </main>
              </div>
            </div>
          ) : (
            <main className="flex-grow w-full" data-testid="unauthenticated-content">
              <Outlet />
            </main>
          )}
        </div>

        {!isAuthenticated && isPublicPage && !isLandingPage && !isGeneratedOfferPage && (
          <footer
            className="bg-card/80 backdrop-blur-sm border-t mt-auto relative z-10"
            data-testid="auth-footer"
          >
            <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
              <p className="text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} ClearOffer. All rights reserved.
              </p>
            </div>
          </footer>
        )}
      </div>
    </SidebarProvider>
  );
};
